import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Trash2, UserPlus, ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { useProfiles } from '@/hooks/useProfiles';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Stage {
  id: string;
  label: string;
  color: string;
}

interface BulkActionsToolbarProps {
  selectedCount: number;
  onClearSelection: () => void;
  onBulkStageUpdate: (newStage: string) => void;
  onBulkOwnerUpdate: (ownerId: string) => void;
  onBulkDelete: () => void;
  stages: Stage[];
}

export function BulkActionsToolbar({
  selectedCount,
  onClearSelection,
  onBulkStageUpdate,
  onBulkOwnerUpdate,
  onBulkDelete,
  stages,
}: BulkActionsToolbarProps) {
  const [selectedStage, setSelectedStage] = useState<string>('');
  const [selectedOwner, setSelectedOwner] = useState<string>('');
  const { profiles = [] } = useProfiles();

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-card border shadow-lg rounded-lg p-4 z-50 min-w-[600px]">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-base px-3 py-1">
            {selectedCount} geselecteerd
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearSelection}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Select value={selectedStage} onValueChange={setSelectedStage}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Wijzig stage..." />
            </SelectTrigger>
            <SelectContent>
              {stages.map((stage) => (
                <SelectItem key={stage.id} value={stage.id}>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${stage.color}`} />
                    {stage.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="secondary"
            size="sm"
            disabled={!selectedStage}
            onClick={() => {
              onBulkStageUpdate(selectedStage);
              setSelectedStage('');
            }}
          >
            <ArrowRight className="w-4 h-4 mr-1" />
            Verplaats
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Select value={selectedOwner} onValueChange={setSelectedOwner}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Wijs toe aan..." />
            </SelectTrigger>
            <SelectContent>
              {profiles.map((profile: any) => (
                <SelectItem key={profile.id} value={profile.id}>
                  {profile.full_name || profile.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="secondary"
            size="sm"
            disabled={!selectedOwner}
            onClick={() => {
              onBulkOwnerUpdate(selectedOwner);
              setSelectedOwner('');
            }}
          >
            <UserPlus className="w-4 h-4 mr-1" />
            Toewijzen
          </Button>
        </div>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" size="sm">
              <Trash2 className="w-4 h-4 mr-1" />
              Archiveren
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Weet je het zeker?</AlertDialogTitle>
              <AlertDialogDescription>
                Je staat op het punt {selectedCount} locatie(s) te archiveren.
                Deze actie kan ongedaan worden gemaakt via het archief.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuleren</AlertDialogCancel>
              <AlertDialogAction onClick={onBulkDelete}>
                Archiveren
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}